import { useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { TerminalFilter } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { Modal } from '@/components/ui/Modal';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { formatDateTime } from '@/lib/format';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { cn } from '@/lib/cn';
import { Can } from '@/lib/permissions';
import { useListHref } from '@/hooks/useListLocation';
import {
  isDefault,
  optionToYesNo,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
  yesNoToOption,
  type ActiveVisibility,
} from '@/hooks/useUrlState';
import {
  TERMINAL_COLUMN_SORT,
  TERMINALS_ADVANCED_KEYS,
  TERMINALS_DEFAULT_SORT,
  terminalsUrlSchema,
} from './terminalsUrlState';

const TerminalsDoc = graphql(/* GraphQL */ `
  query Terminals(
    $filter: TerminalFilter
    $includeInactive: Boolean
    $page: Int
    $pageSize: Int
    $sortField: String
    $sortDir: String
  ) {
    terminals(
      filter: $filter
      includeInactive: $includeInactive
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortDir: $sortDir
    ) {
      nodes {
        id
        name
        poiid
        isActive
        connected
        batteryLevel
        createdAt
        updatedAt
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const SetTerminalsActiveDoc = graphql(/* GraphQL */ `
  mutation SetTerminalsActive($ids: [ID!]!, $isActive: Boolean!) {
    setTerminalsActive(ids: $ids, isActive: $isActive) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`);

const SyncTerminalsDoc = graphql(/* GraphQL */ `
  mutation SyncTerminals {
    syncTerminals {
      added {
        name
        poiid
      }
      reactivated {
        name
        poiid
      }
      deactivated {
        name
        poiid
      }
      skippedPoiids
    }
  }
`);

const ExportTerminalsCsvDoc = graphql(/* GraphQL */ `
  query ExportTerminalsCsv($filter: TerminalFilter, $includeInactive: Boolean, $sortField: String, $sortDir: String) {
    exportTerminalsCsv(filter: $filter, includeInactive: $includeInactive, sortField: $sortField, sortDir: $sortDir) {
      url
      expiresAt
    }
  }
`);

const filterSectionTitle =
  'mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const filterLabel = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';

interface SyncLine {
  name: string;
  poiid: string;
}

type SyncDialog =
  | {
      kind: 'ok';
      added: SyncLine[];
      reactivated: SyncLine[];
      deactivated: SyncLine[];
      skippedPoiids: string[];
    }
  | { kind: 'error'; message: string };

interface TerminalRow {
  id: string;
  name: string;
  poiid: string;
  isActive: boolean;
  connected: boolean;
  batteryLevel: number;
  createdAt: string;
  updatedAt: string;
}

type TerminalAction = 'SET_ACTIVE' | 'SET_INACTIVE';

function BatteryBar({ level }: { level: number }) {
  const clamped = Math.max(0, Math.min(100, level));
  const tone =
    clamped <= 25
      ? 'bg-red-500'
      : clamped <= 50
        ? 'bg-amber-500'
        : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
        <div
          className={cn('h-full rounded-full transition-all', tone)}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {clamped}%
      </span>
    </div>
  );
}

export function TerminalsListPage() {
  const apollo = useApolloClient();
  const navigate = useNavigate();
  const { state: url, patch, setFilters, reset } = useUrlState(terminalsUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  const [batteryMin, setBatteryMin] = useDebouncedUrlField(url.batteryMin, (next) => setFilters({ batteryMin: next }));
  const [batteryMax, setBatteryMax] = useDebouncedUrlField(url.batteryMax, (next) => setFilters({ batteryMax: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => TERMINALS_ADVANCED_KEYS.some((key) => !isDefault(terminalsUrlSchema, url, key)));
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, TERMINALS_DEFAULT_SORT));
  };

  const sortField = TERMINAL_COLUMN_SORT[url.sort];
  const sortDir = url.dir === 'desc' ? 'DESC' : 'ASC';
  const includeInactive = url.show !== 'active';

  const filter = useMemo((): TerminalFilter => {
    const out: TerminalFilter = {};
    if (url.search.trim()) {
      out.search = url.search.trim();
    }
    if (url.connected !== null) {
      out.connected = url.connected;
    }
    if (url.batteryMin !== '' || url.batteryMax !== '') {
      out.battery = {
        min: url.batteryMin === '' ? null : Number(url.batteryMin),
        max: url.batteryMax === '' ? null : Number(url.batteryMax),
      };
    }
    if (url.show === 'inactive') {
      out.isActive = false;
    }
    return out;
  }, [url]);

  const { data, previousData, loading, refetch } = useQuery(TerminalsDoc, {
    variables: { filter, includeInactive, page: url.page, pageSize: url.size, sortField, sortDir },
  });
  const terminalsData = data ?? previousData;
  const [setTerminalsActive] = useMutation(SetTerminalsActiveDoc);
  const [syncTerminals] = useMutation(SyncTerminalsDoc);
  const [syncing, setSyncing] = useState(false);
  const [syncDialog, setSyncDialog] = useState<SyncDialog | null>(null);

  const rows: TerminalRow[] = useMemo(
    () =>
      (terminalsData?.terminals.nodes ?? []).map((n) => ({
        id: n.id,
        name: n.name,
        poiid: n.poiid,
        isActive: n.isActive,
        connected: n.connected,
        batteryLevel: n.batteryLevel ?? 0,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
      })),
    [terminalsData],
  );
  const pageInfo = terminalsData?.terminals.pageInfo;

  const columns = useMemo<ColumnDef<TerminalRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/terminals/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'poiid',
        accessorKey: 'poiid',
        header: 'POIID',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
            {row.original.poiid}
          </span>
        ),
      },
      {
        id: 'battery',
        accessorFn: (row) => row.batteryLevel,
        header: 'Battery',
        cell: ({ row }) => <BatteryBar level={row.original.batteryLevel} />,
      },
      {
        id: 'connected',
        accessorKey: 'connected',
        header: 'Connected',
        cell: ({ row }) => (
          <Badge tone={row.original.connected ? 'success' : 'neutral'}>
            {row.original.connected ? 'Connected' : 'Offline'}
          </Badge>
        ),
      },
      {
        id: 'active',
        accessorKey: 'isActive',
        header: 'Active',
        cell: ({ row }) => (
          <Badge tone={row.original.isActive ? 'success' : 'neutral'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
      },
    ],
    [listHref],
  );

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportTerminalsCsvDoc,
      variables: { filter, includeInactive, sortField, sortDir },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportTerminalsCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportTerminalsCsv.url);
    }
  };

  const handleBulkActive = async (ids: string[], active: boolean) => {
    await setTerminalsActive({ variables: { ids, isActive: active } });
    setSelection({});
    await refetch();
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncTerminals();
      const sync = result.data?.syncTerminals;
      if (!sync) {
        setSyncDialog({
          kind: 'error',
          message: 'Could not sync terminals from Adyen.',
        });
        return;
      }
      setSyncDialog({
        kind: 'ok',
        added: sync.added,
        reactivated: sync.reactivated,
        deactivated: sync.deactivated,
        skippedPoiids: sync.skippedPoiids,
      });
      await refetch();
    } catch (err) {
      setSyncDialog({
        kind: 'error',
        message: graphqlErrorMessage(err, 'Could not sync terminals from Adyen.'),
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Terminals
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Payment terminals in the current scope. By default only active terminals are shown;
            widen Active status to see inactive or all. Edit a terminal to change its flags.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Can permission="terminal:manage">
            <Button
              id="admin.terminals.sync"
              variant="outline"
              loading={syncing}
              onClick={() => void handleSync()}
            >
              Sync from Adyen
            </Button>
          </Can>
          <Button
            id="admin.terminals.new"
            onClick={() => navigate('/terminals/new', { state: { from: listHref } })}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New terminal
          </Button>
        </div>
      </div>

      <SyncResultModal
        dialog={syncDialog}
        onClose={() => setSyncDialog(null)}
      />

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.terminals.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search terminals…',
        }}
        primary={
          <button
            type="button"
            onClick={reset}
            className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-xs transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Reset all filters
          </button>
        }
        advanced={
          <div className="w-full min-w-0 space-y-10">
            <section className="min-w-0" aria-labelledby="terminals-filters-visibility-heading">
              <h3 id="terminals-filters-visibility-heading" className={filterSectionTitle}>
                Terminal visibility
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                <label className="min-w-0 lg:col-span-3">
                  <span className={filterLabel}>Active status</span>
                  <Select
                    id="admin.terminals.show"
                    className="w-full min-w-0"
                    value={url.show}
                    onChange={(e) =>
                      setFilters({ show: e.target.value as ActiveVisibility })
                    }
                    options={[
                      { value: 'active', label: 'Active only' },
                      { value: 'inactive', label: 'Inactive only' },
                      { value: 'all', label: 'All terminals' },
                    ]}
                  />
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="terminals-filters-connection-heading"
            >
              <h3 id="terminals-filters-connection-heading" className={filterSectionTitle}>
                Connection and battery
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-12">
                <label className="min-w-0 sm:col-span-1 lg:col-span-3">
                  <span className={filterLabel}>Connected</span>
                  <Select
                    id="admin.terminals.connected"
                    className="w-full min-w-0"
                    value={yesNoToOption(url.connected)}
                    onChange={(event) => setFilters({ connected: optionToYesNo(event.target.value) })}
                    options={[
                      { value: '', label: 'Any' },
                      { value: 'yes', label: 'Connected' },
                      { value: 'no', label: 'Offline' },
                    ]}
                  />
                </label>
                <label className="min-w-0 sm:col-span-1 lg:col-span-2">
                  <span className={filterLabel}>Battery min (%)</span>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={batteryMin}
                    onChange={(event) => setBatteryMin(event.target.value)}
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0 sm:col-span-1 lg:col-span-2">
                  <span className={filterLabel}>Battery max (%)</span>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={batteryMax}
                    onChange={(event) => setBatteryMax(event.target.value)}
                    className="w-full min-w-0"
                  />
                </label>
              </div>
            </section>
          </div>
        }
      />

      <DataGrid<TerminalRow, TerminalAction>
        data={rows}
        columns={columns}
        loading={loading && !terminalsData}
        rowId={(row) => row.id}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        onExport={handleExport}
        selection={{ state: selection, onChange: setSelection }}
        actions={[
          {
            id: 'SET_ACTIVE',
            label: 'Activate',
            variant: 'outline',
            onRun: (selected) =>
              handleBulkActive(
                selected.map((r) => r.id),
                true,
              ),
          },
          {
            id: 'SET_INACTIVE',
            label: 'Deactivate',
            variant: 'outline',
            onRun: (selected) =>
              handleBulkActive(
                selected.map((r) => r.id),
                false,
              ),
          },
        ]}
        allowedActionsForRow={(row) =>
          row.isActive ? ['SET_INACTIVE'] : ['SET_ACTIVE']
        }
        rowMuted={(row) => !row.isActive}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}

function SyncResultModal({
  dialog,
  onClose,
}: {
  dialog: SyncDialog | null;
  onClose: () => void;
}) {
  const unchanged =
    dialog?.kind === 'ok' &&
    dialog.added.length === 0 &&
    dialog.reactivated.length === 0 &&
    dialog.deactivated.length === 0 &&
    dialog.skippedPoiids.length === 0;
  return (
    <Modal
      open={dialog !== null}
      onClose={onClose}
      title="Sync from Adyen"
      description={
        dialog?.kind === 'error'
          ? 'The sync did not change this scope.'
          : 'This scope was compared with Adyen.'
      }
      footer={
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      }
    >
      {dialog?.kind === 'error' ? (
        <p id="admin.terminals.syncError" role="alert" className="text-red-600 dark:text-red-400">
          {dialog.message}
        </p>
      ) : null}
      {dialog?.kind === 'ok' && unchanged ? (
        <p id="admin.terminals.syncResult">Nothing changed. Adyen's list already matched this scope.</p>
      ) : null}
      {dialog?.kind === 'ok' && !unchanged ? (
        <div id="admin.terminals.syncResult" className="space-y-4">
          <SyncLineList title="Added" lines={dialog.added} />
          <SyncLineList title="Turned back on" lines={dialog.reactivated} />
          <SyncLineList title="Deactivated" lines={dialog.deactivated} />
          {dialog.skippedPoiids.length > 0 ? (
            <section>
              <h3 className="font-medium text-gray-900 dark:text-white">Not stored</h3>
              <p className="mt-1 text-amber-800 dark:text-amber-200">
                Adyen refused the logo, so these terminals were not stored.
              </p>
              <ul className="mt-2 space-y-1 font-mono text-xs">
                {dialog.skippedPoiids.map((poiid) => (
                  <li key={poiid}>{poiid}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function SyncLineList({ title, lines }: { title: string; lines: SyncLine[] }) {
  if (lines.length === 0) return null;
  return (
    <section>
      <h3 className="font-medium text-gray-900 dark:text-white">
        {title} ({lines.length})
      </h3>
      <ul className="mt-2 space-y-1">
        {lines.map((line) => (
          <li key={line.poiid}>
            {line.name}{' '}
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{line.poiid}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
